// src/components/common/DebugHelper.js
import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Button, 
  Collapse, 
  Divider 
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import BugReportIcon from '@mui/icons-material/BugReport';

/**
 * Debug Helper component for displaying debug information in development
 * @param {Object} props
 * @param {any} props.data - Data to debug
 * @param {string} props.title - Debug section title
 * @param {boolean} props.initialExpanded - Whether debug is initially expanded
 * @param {boolean} props.prettify - Whether to pretty print JSON
 */
const DebugHelper = ({ 
  data, 
  title = 'Debug Data', 
  initialExpanded = false,
  prettify = true
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  
  const isDebugEnvironment = () => {
    // Check if we're on staging or the dashboard
    const isDcfraudDomain = window.location.hostname.includes('dcfraud');
    
    // Check if debug mode is enabled via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const showDebug = urlParams.get('showDebug') === 'true';
    
    // Show if we're on staging OR we're on dcfraud and showDebug is true
    return window.location.hostname.includes('staging') || 
           (isDcfraudDomain && showDebug) ||
           isDcfraudDomain; // You can remove this line if you want debug to only show with parameter
  };
  
  // Show in development or on approved domains with conditions
  if (process.env.NODE_ENV !== 'development' && !isDebugEnvironment()) {
    return null;
  }
  
  // Format data for display
  const formatData = (data) => {
    try {
      if (prettify) {
        return JSON.stringify(data, null, 2);
      } else {
        return JSON.stringify(data);
      }
    } catch (error) {
      return `[Error displaying data: ${error.message}]`;
    }
  };
  
  // Check for react elements that would cause rendering errors
  const findReactElements = (obj) => {
    const results = [];
    
    const traverse = (obj, path = '') => {
      if (!obj || typeof obj !== 'object') return;
      
      if (obj.$$typeof && obj._owner) {
        results.push(path || 'root');
      }
      
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          traverse(obj[key], path ? `${path}.${key}` : key);
        }
      }
    };
    
    traverse(obj);
    return results;
  };
  
  const reactElements = findReactElements(data);
  
  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2, 
        border: '1px dashed #ccc',
        bgcolor: '#f5f5f5'
      }}
    >
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer' }}
      >
        <Box display="flex" alignItems="center">
          <BugReportIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle1" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Button 
          size="small" 
          variant="outlined" 
          color="inherit"
          startIcon={<CodeIcon />}
        >
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </Box>
      
      <Collapse in={expanded}>
        <Divider sx={{ my: 1 }} />
        
        {reactElements.length > 0 && (
          <Box mb={2} p={1} bgcolor="#fff3cd" borderRadius={1}>
            <Typography variant="caption" color="warning.dark">
              Warning: React elements detected at: {reactElements.join(', ')}
              These cannot be stringified and may cause rendering errors.
            </Typography>
          </Box>
        )}
        
        <Box 
          component="pre" 
          sx={{ 
            p: 1, 
            bgcolor: 'background.paper', 
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '0.8rem',
          }}
        >
          {formatData(data)}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DebugHelper;